import * as Device from 'expo-device';
import * as Location from 'expo-location';
import * as Notifications from 'expo-notifications';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';

import { useAuth } from '@/components/providers/AuthProvider';

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Resolves once `AppState.currentState === 'active'`. On Android, the activity
 * can briefly report 'inactive' immediately after sign-in while the navigation
 * transition is still settling — issuing `requestPermissions` in that window
 * has empirically led to the OS silently dropping the call in release builds.
 */
const waitUntilActive = (timeoutMs = 5000): Promise<void> =>
  new Promise<void>((resolve) => {
    if (AppState.currentState === 'active') {
      resolve();
      return;
    }
    const timeout = setTimeout(() => {
      sub.remove();
      resolve();
    }, timeoutMs);
    const sub = AppState.addEventListener('change', (next) => {
      if (next === 'active') {
        clearTimeout(timeout);
        sub.remove();
        resolve();
      }
    });
  });

/**
 * Yields to the JS event loop so any in-flight navigation/animation work
 * (e.g. the post-auth route transition) has a chance to flush before we fire
 * the first runtime permission dialog.
 */
const waitForInteractions = (): Promise<void> =>
  new Promise<void>((resolve) => setTimeout(resolve, 0));

/**
 * Polls a permission accessor until the status leaves "undetermined" or the
 * timeout fires. Used to confirm Android has actually finished tearing down a
 * runtime-permission dialog before we kick off the next one — relying solely
 * on `requestPermissionsAsync()` resolving has proven fragile in release
 * builds, where the JS promise can resolve a beat before the native dialog
 * window is fully detached and the next `ActivityCompat.requestPermissions`
 * call gets silently dropped.
 *
 * Note: on Android 13+ expo-notifications reports a never-asked permission as
 * 'denied' rather than 'undetermined', so this helper bails on any non-
 * 'undetermined' status (including 'denied') — that's fine because both 'denied'
 * and 'granted' indicate the dialog has fully closed.
 */
const waitUntilSettled = async (
  getStatus: () => Promise<{ status: string }>,
  maxAttempts = 60
): Promise<void> => {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const { status } = await getStatus();
      if (status !== 'undetermined') return;
    } catch {}
    await wait(100);
  }
};

/**
 * Drives the post-login runtime permission flow in a single deterministic
 * sequence:
 *
 *   1. Push notifications (Android 13+ POST_NOTIFICATIONS, iOS APN)
 *   2. Foreground location (when-in-use)
 *
 * Why this exists separately from PushProvider/LocationProvider:
 * Independently triggering these requests from each provider races on Android
 * — only one runtime permission dialog can be shown at a time, and a second
 * `ActivityCompat.requestPermissions(...)` call issued while another is in
 * flight gets silently dropped. The previous JS-level queue worked in dev but
 * proved unreliable in release builds (faster microtask scheduling and tighter
 * dialog tear-down timing). Centralising the flow here guarantees the dialogs
 * are presented in order, with each request waiting for the previous OS-side
 * status to actually settle before the next one fires.
 *
 * The two domain providers (PushProvider, LocationProvider) are reduced to
 * permission *observers*: they read the current state via `getPermissionsAsync`
 * and act when granted, but never call `requestPermissionsAsync` from the
 * post-login auth effect.
 */
interface PermissionsContextValue {
  /**
   * Increments after every permission request resolves (granted or denied).
   * Downstream providers (PushProvider, LocationProvider) depend on this in
   * their effects to re-check status as soon as the dialog dismisses, even on
   * iOS where AppState doesn't usefully transition during a system permission
   * dialog.
   */
  permissionsTick: number;
  /**
   * False while the native startup permission sequence is checking/requesting
   * permissions. Consumers can use this to avoid presenting their own modal UI
   * over OS permission dialogs.
   */
  isPermissionBootstrapReady: boolean;
}

const PermissionsContext = createContext<PermissionsContextValue>({
  permissionsTick: 0,
  isPermissionBootstrapReady: Platform.OS === 'web',
});

export const usePermissionsTick = (): number =>
  useContext(PermissionsContext).permissionsTick;

export const usePermissionBootstrapReady = (): boolean =>
  useContext(PermissionsContext).isPermissionBootstrapReady;

export const PermissionsBootstrap: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [permissionsTick, setPermissionsTick] = useState(0);
  const [isPermissionBootstrapReady, setIsPermissionBootstrapReady] = useState(Platform.OS === 'web');
  const bumpTick = useRef(() => setPermissionsTick((t) => t + 1));
  bumpTick.current = () => setPermissionsTick((t) => t + 1);
  // Run-once guard scoped to the current authenticated session. Reset when the
  // user signs out so the next sign-in can re-prompt if anything is still
  // requestable (e.g. permissions previously denied with `canAskAgain: true`).
  const hasRunForSession = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      hasRunForSession.current = false;
      setIsPermissionBootstrapReady(Platform.OS === 'web');
      return;
    }
    if (Platform.OS === 'web') {
      setIsPermissionBootstrapReady(true);
      return;
    }
    //if (!Device.isDevice) return;
    if (hasRunForSession.current) {
      setIsPermissionBootstrapReady(true);
      return;
    }
    hasRunForSession.current = true;
    setIsPermissionBootstrapReady(false);

    let cancelled = false;

    // "Can we still prompt?" Treats anything that is not yet granted but where
    // the OS will allow another prompt as eligible.
    //   - Android 13+: expo-notifications reports a never-asked
    //     POST_NOTIFICATIONS as status 'denied' (canAskAgain: true) rather than
    //     'undetermined', so we can't gate on 'undetermined' alone.
    //   - Android "Ask every time" / one-time location grants: status may be
    //     'granted' on next launch but `expires` is a timestamp instead of
    //     'never', signalling the grant is per-session and we should re-prompt.
    const shouldPrompt = (resp: {
      status: string;
      canAskAgain?: boolean;
      expires?: 'never' | number;
    }) => {
      if (resp.canAskAgain === false) return false;
      if (resp.status !== 'granted') return true;
      if (resp.expires !== undefined && resp.expires !== 'never') return true;
      return false;
    };

    const run = async () => {
      // Readiness gate: on Android, the post-sign-in render happens while the
      // activity is still mid-transition. Issuing `requestPermissions` in that
      // window has been observed to be silently dropped by the OS in release
      // builds (dev builds are slow enough that this race rarely triggers).
      // Wait for the app to be `active`, let the interaction queue flush, then
      // give the activity a final beat before kicking off the sequence.
      if (Platform.OS === 'android') {
        await waitUntilActive();
        if (cancelled) return;
        await waitForInteractions();
        if (cancelled) return;
        await wait(2000);
        if (cancelled) return;
      }

      try {
        const notif = await Notifications.getPermissionsAsync();
        if (cancelled) return;
        const notifShould = shouldPrompt(notif);
        if (__DEV__) console.log('[PermissionsBootstrap] notifications snapshot', notif, '→ prompt:', notifShould);

        if (notifShould) {
          const result = await Notifications.requestPermissionsAsync();
          if (__DEV__) console.log('[PermissionsBootstrap] notifications request result', result);
          if (cancelled) return;

          await waitUntilSettled(() => Notifications.getPermissionsAsync());
          if (cancelled) return;

          // Notify observers (PushProvider) that the OS state has changed.
          bumpTick.current();

          // Extra buffer to let Android tear down the dialog window before the
          // next `requestPermissions` lands. Empirically required on some OEM
          // skins; cheap on others.
          if (Platform.OS === 'android') await wait(400);
          if (cancelled) return;
        }
      } catch (error) {
        console.warn('[PermissionsBootstrap] Notifications request failed', error);
      }

      try {
        const loc = await Location.getForegroundPermissionsAsync();
        if (cancelled) return;
        const locShould = shouldPrompt(loc);
        if (__DEV__) console.log('[PermissionsBootstrap] location snapshot', loc, '→ prompt:', locShould);

        if (locShould) {
          const result = await Location.requestForegroundPermissionsAsync();
          if (__DEV__) console.log('[PermissionsBootstrap] location request result', result);
          if (cancelled) return;

          // Notify observers (LocationProvider) that the OS state has changed.
          bumpTick.current();
        }
      } catch (error) {
        console.warn('[PermissionsBootstrap] Location request failed', error);
      }

      if (!cancelled) {
        setIsPermissionBootstrapReady(true);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const contextValue = useMemo(
    () => ({ permissionsTick, isPermissionBootstrapReady }),
    [permissionsTick, isPermissionBootstrapReady]
  );

  return (
    <PermissionsContext.Provider value={contextValue}>{children}</PermissionsContext.Provider>
  );
};
